/**
 * Datadog client-side integration: RUM, browser logs, product-analytics helpers,
 * and the browser feature-flag client. Server helpers live in `./datadog-server.ts`
 * so their Node-only deps never end up in the browser bundle.
 *
 * Public env vars (NEXT_PUBLIC_*, exposed to the browser):
 *   NEXT_PUBLIC_DD_APPLICATION_ID   RUM application id
 *   NEXT_PUBLIC_DD_CLIENT_TOKEN     RUM/logs client token
 *   NEXT_PUBLIC_DD_SITE             e.g. "datadoghq.com"
 *   NEXT_PUBLIC_DD_SERVICE          service name (default "appo-web")
 *   NEXT_PUBLIC_DD_ENV              e.g. "prod" / "staging" / "dev"
 *   NEXT_PUBLIC_APP_VERSION         git sha or release tag
 */

type DatadogUser = { id: string | number; email?: string; name?: string };

const DD_SITE = process.env.NEXT_PUBLIC_DD_SITE ?? "datadoghq.com";
const DD_SERVICE = process.env.NEXT_PUBLIC_DD_SERVICE ?? "appo-web";
const DD_ENV = process.env.NEXT_PUBLIC_DD_ENV ?? process.env.NODE_ENV ?? "development";
const DD_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? "0.0.0";

// ────────────────────────────────────────────────────────────────────────────
// RUM + browser logs
// ────────────────────────────────────────────────────────────────────────────

let clientInitialized = false;

export function initDatadogClient() {
  if (typeof window === "undefined" || clientInitialized) return;

  const applicationId = process.env.NEXT_PUBLIC_DD_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN;
  if (!applicationId || !clientToken) return;

  void Promise.all([
    import("@datadog/browser-rum"),
    import("@datadog/browser-rum-react"),
  ]).then(([{ datadogRum }, { reactPlugin }]) => {
    datadogRum.init({
      applicationId,
      clientToken,
      site: DD_SITE,
      service: DD_SERVICE,
      env: DD_ENV,
      version: DD_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: "mask-user-input",
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      trackViewsManually: false,
      // Propagate trace context to same-origin fetch/XHR so browser errors link
      // to backend APM traces in Error Tracking.
      allowedTracingUrls: [(url: string) => url.startsWith(window.location.origin)],
      plugins: [reactPlugin()],
    });
  });

  void import("@datadog/browser-logs").then(({ datadogLogs }) => {
    datadogLogs.init({
      clientToken,
      site: DD_SITE,
      service: DD_SERVICE,
      env: DD_ENV,
      version: DD_VERSION,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["error", "warn"],
      sessionSampleRate: 100,
    });
  });

  clientInitialized = true;
}

export async function identifyDatadogUser(user: DatadogUser) {
  if (typeof window === "undefined") return;
  const [{ datadogRum }, { datadogLogs }] = await Promise.all([
    import("@datadog/browser-rum"),
    import("@datadog/browser-logs"),
  ]);
  const ctx = { id: String(user.id), email: user.email, name: user.name };
  datadogRum.setUser(ctx);
  datadogLogs.setUser(ctx);
}

// ────────────────────────────────────────────────────────────────────────────
// Product analytics + client-side error tracking
// ────────────────────────────────────────────────────────────────────────────

export async function trackAction(name: string, context?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const { datadogRum } = await import("@datadog/browser-rum");
  datadogRum.addAction(name, context);
}

export async function trackClientError(error: unknown, context?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const { datadogRum } = await import("@datadog/browser-rum");
  datadogRum.addError(error, context);
}

export async function setGlobalContext(context: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const { datadogRum } = await import("@datadog/browser-rum");
  Object.entries(context).forEach(([k, v]) => datadogRum.setGlobalContextProperty(k, v));
}

// ────────────────────────────────────────────────────────────────────────────
// Feature flags — client (OpenFeature)
// ────────────────────────────────────────────────────────────────────────────

let ffClientReady: Promise<void> | null = null;

export async function getClientFeatureFlags() {
  const { OpenFeature } = await import("@openfeature/web-sdk");
  if (!ffClientReady) {
    ffClientReady = (async () => {
      const { DatadogProvider } = await import("@datadog/openfeature-browser");
      const clientToken = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN ?? "";
      const applicationId = process.env.NEXT_PUBLIC_DD_APPLICATION_ID;
      await OpenFeature.setProviderAndWait(
        new DatadogProvider({
          clientToken,
          applicationId,
          site: DD_SITE,
          service: DD_SERVICE,
          env: DD_ENV,
          version: DD_VERSION,
          enableExposureLogging: true,
        }),
      );
    })();
  }
  await ffClientReady;
  return OpenFeature.getClient();
}
